import { useCallback, useEffect, useMemo, useState } from "react";
import { OnboardingAuthoring } from "./onboarding-authoring";
import { OnboardingProgress } from "./onboarding-progress";
import { RichTextView } from "./rich-text-editor";
import type { TiptapDoc } from "./rich-text-doc";
import { ErrorBanner, Skeleton, useConfirm, useToast } from "./ui-kit";
import { toVideoEmbed } from "./video-embed";
import { formatServerDateTime } from "./server-time";

type Member = { displayName: string; id: string };

// The learner's onboarding view + the mentor's review queue -- delivery
// slices 3 and 4 of docs/plans/2026-08-pm-onboarding-lms.md. The authoring
// side lives in onboarding-authoring.tsx.

type TaskState = "locked" | "open" | "submitted" | "approved" | "rejected";
type EvidenceKind = "comment" | "attachment" | "link" | "work_item" | "acknowledgement" | "any";

type AssignmentSummary = {
  id: string; planKey: string; planTitle: string; teamId: string;
  userId: string; mentorUserId: string; learnerName: string; mentorName: string;
  startedOn: string; targetOn: string | null; status: string;
  taskCount: number; approvedCount: number;
  overdueCount: number; oldestSubmittedAt: string | null; lastActivityAt: string | null;
};

type AssignmentTask = {
  id: string; key: string; title: string; body: TiptapDoc | null;
  state: TaskState; attempt: number; occurrence: number;
  workItemId: string; taskKey: string | null; dueOn: string | null;
  evidenceKind: EvidenceKind; evidenceMinChars: number | null; evidencePrompt: string | null;
  requiresApproval: boolean; recurrence: "none" | "weekly";
  prerequisiteKeys: string[]; reviewNote: string | null; submittedAt: string | null;
  videoUrl: string | null; videoTitle: string | null;
  latestEvidence: TiptapDoc | null;
  // The onboarding task's own work_item is deliberately hidden from the
  // board, so a genuine second work_item -- an ordinary board task, created
  // on demand -- is what "Open task" actually links to once one exists.
  trackingWorkItemId: string | null; trackingTaskKey: string | null; trackingSprintId: string | null;
};

type AssignmentStage = {
  id: string; key: string; title: string; description: TiptapDoc | null;
  kind: "sequential" | "parallel"; position: number;
  tasks: AssignmentTask[];
};

type QuizState = {
  id: string; key: string; title: string; intro: TiptapDoc | null;
  trigger: string; stageId: string | null; gateStatus: "not_triggered" | "armed" | "passed";
  passThresholdPct: number; questionCount: number; attemptsUsed: number; maxAttempts: number | null;
  cooldownHours: number; canStart: boolean; openAttemptId: string | null;
  lastOutcome: string | null; retryAfter: string | null;
  onFail: string; gradableAttemptId: string | null;
};

type AttemptView = {
  attempt: { id: string; title: string; outcome: string; scorePct: number | null };
  canGrade: boolean;
  questions: Array<{
    id: string; kind: string; body: unknown; textAnswer: string | null;
    pointsPossible: number; pointsAwarded: number | null; isCorrect: boolean | null;
    options: Array<{ id: string; body: string; selected: boolean; correct?: boolean }>;
  }>;
};

type QuizQuestion = {
  id: string; kind: "single_choice" | "multi_choice" | "short_text";
  body: unknown; points: number; options: Array<{ id: string; body: string }>;
};

type AssignmentDetail = {
  assignment: AssignmentSummary;
  canReview: boolean;
  isLearner: boolean;
  stages: AssignmentStage[];
  quizzes: QuizState[];
};

const api = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: init?.body ? { "content-type": "application/json", ...init.headers } : init?.headers,
  });
  const payload = (await response.json()) as T & { message?: string };
  if (!response.ok) throw new Error(payload.message || "Taskei could not complete that request.");
  return payload;
};

type TrackingTaskResult = { alreadyExisted: boolean; sprintId: string | null; taskKey: string | null; workItemId: string };

const createTrackingTask = (assignmentTaskId: string) =>
  api<TrackingTaskResult>(`/api/onboarding/assignment-tasks/${encodeURIComponent(assignmentTaskId)}/tracking-task`, { method: "POST" });

// Mirrors portalRoutePath's /teams/:id/tasks/:taskId handling in
// portal-routes.ts: board mode auto-selects the active sprint when none is
// named, but never shows a backlog item, so a task with no sprint needs an
// explicit ?view=backlog or the board simply never loads it -- the same
// silent-failure shape as the original broken "Open task" link.
const trackingTaskHref = (teamId: string, workItemId: string, sprintId: string | null) =>
  `/teams/${encodeURIComponent(teamId)}/tasks/${encodeURIComponent(workItemId)}${
    sprintId ? `?sprint=${encodeURIComponent(sprintId)}` : "?view=backlog"
  }`;

const asDoc = (text: string) => ({
  type: "doc",
  content: [{ type: "paragraph", content: text ? [{ type: "text", text }] : [] }],
});

// Leads a comment with an @mention of the other party so posting from the
// onboarding view rides the task-comments API's existing mention-notification
// path (see notifyTaskMentions in tasks-api.ts) instead of needing a new
// notification category just for this back-channel.
const mentionDoc = (mentionId: string, mentionLabel: string, text: string): TiptapDoc => ({
  type: "doc",
  content: [{
    type: "paragraph",
    content: [
      { type: "mention", attrs: { id: mentionId, label: mentionLabel } },
      ...(text ? [{ type: "text", text: ` ${text}` }] : []),
    ],
  }],
}) as TiptapDoc;

type DiscussionComment = {
  attachments: Array<{ contentType: string; fileName: string; id: string; sizeBytes: number }>;
  authorId: string; authorName: string; body: TiptapDoc; createdAt: string; id: string;
};

// Shared by EvidenceForm's inline file upload and TaskDiscussion's composer --
// both just need "presign, PUT the file, then attach it to a comment on the
// materialised work item", the same three calls task-workspace.tsx makes.
const uploadTaskAttachment = async (workItemId: string, file: File, body: unknown) => {
  const presign = await api<{ attachmentId: string; key: string; uploadUrl: string }>(`/api/tasks/${encodeURIComponent(workItemId)}/attachments/presign`, {
    method: "POST",
    body: JSON.stringify({ contentType: file.type || "application/octet-stream", fileName: file.name, sizeBytes: file.size }),
  });
  const uploadResponse = await fetch(presign.uploadUrl, { method: "PUT", body: file });
  if (!uploadResponse.ok) throw new Error("Could not upload the file.");
  await api(`/api/tasks/${encodeURIComponent(workItemId)}/comments`, {
    method: "POST",
    body: JSON.stringify({
      attachments: [{ attachmentId: presign.attachmentId, contentType: file.type || "application/octet-stream", fileName: file.name, key: presign.key, sizeBytes: file.size }],
      body,
    }),
  });
};

const STATE_BADGE: Record<TaskState, { className: string; label: string }> = {
  approved: { className: "text-bg-success", label: "Approved" },
  locked: { className: "text-bg-secondary", label: "Locked" },
  open: { className: "text-bg-primary", label: "To do" },
  rejected: { className: "text-bg-danger", label: "Changes requested" },
  submitted: { className: "text-bg-warning", label: "Awaiting review" },
};

const EVIDENCE_LABEL: Record<EvidenceKind, string> = {
  acknowledgement: "Acknowledge to complete",
  any: "Attach any evidence",
  attachment: "Attach a file, then confirm",
  comment: "Write your evidence",
  link: "Provide a link",
  work_item: "Link a work item",
};

// A due date is the main time signal in a 12-week plan, so it earns colour
// rather than sitting in the same grey as everything else.
const dueMeta = (dueOn: string | null): { className: string; label: string } | null => {
  if (!dueOn) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((new Date(`${dueOn}T00:00:00`).getTime() - today.getTime()) / 86_400_000);
  const plural = (count: number) => (count === 1 ? "day" : "days");
  if (Number.isNaN(days)) return { className: "text-body-secondary", label: `due ${dueOn}` };
  if (days < 0) return { className: "text-danger fw-semibold", label: `overdue by ${-days} ${plural(-days)}` };
  if (days === 0) return { className: "text-warning-emphasis fw-semibold", label: "due today" };
  if (days <= 3) return { className: "text-warning-emphasis", label: `due in ${days} ${plural(days)}` };
  return { className: "text-body-secondary", label: `due ${dueOn}` };
};

// Mirrors task-workspace.tsx's oneBusinessDayCutoff/isStaleForScrum: a
// submission sitting past the previous business day (skipping straight over
// a weekend) is the same "gone quiet" signal the daily-scrum flag uses for
// comments. Nothing else in the review queue aged a submission before this --
// a forgotten approval just sat there silently blocking the learner.
const oneBusinessDayCutoff = (now: Date) => {
  const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  cutoff.setDate(cutoff.getDate() - 1);
  const day = cutoff.getDay();
  if (day === 0) cutoff.setDate(cutoff.getDate() - 2);
  else if (day === 6) cutoff.setDate(cutoff.getDate() - 1);
  return cutoff;
};

const reviewAge = (submittedAt: string | null): { className: string; label: string; stale: boolean } | null => {
  if (!submittedAt) return null;
  const submitted = new Date(submittedAt);
  const days = Math.max(0, Math.floor((Date.now() - submitted.getTime()) / 86_400_000));
  const stale = submitted.getTime() < oneBusinessDayCutoff(new Date()).getTime();
  const plural = (count: number) => (count === 1 ? "day" : "days");
  if (days === 0 && !stale) return { className: "text-body-secondary", label: "submitted today", stale };
  return {
    className: stale ? "text-danger fw-semibold" : "text-body-secondary",
    label: stale ? `waiting ${days} ${plural(days)} — overdue for review` : `waiting ${days} ${plural(days)}`,
    stale,
  };
};

// Mirrors the server's own link check (EVIDENCE_REQUIRED in onboarding-api.ts,
// /^https?:\/\/\S+$/), but a bare domain like "docs.google.com/doc/abc" is
// the ordinary case for a pasted link, so this fills in the scheme rather
// than just rejecting it -- returns null only for something still malformed
// (whitespace inside, or nothing typed) once that scheme is added.
const normalizeUrl = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  return /^https?:\/\/\S+$/.test(withScheme) ? withScheme : null;
};

const ON_FAIL_CONSEQUENCE: Record<string, string> = {
  reset_plan: "every task on this plan reopens and must be redone",
  reset_stage: "every task in the gated stage reopens and must be redone",
  retry_quiz: "nothing resets — you simply retake it",
};

function EvidenceForm({ task, teamId, onDone }: { task: AssignmentTask; teamId: string; onDone: () => void }) {
  // A task sent back for changes starts from what the learner already wrote:
  // being asked to revise and handed an empty box is how you get a shorter,
  // worse second attempt than the first.
  const [text, setText] = useState(() =>
    task.state === "rejected" ? docToEditableText(task.latestEvidence) : "");
  const [url, setUrl] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [workItemId, setWorkItemId] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const [attachedFileName, setAttachedFileName] = useState<string | null>(null);
  const showToast = useToast();

  // A rejected-and-resubmitted attachment task may already have a file from
  // the previous attempt sitting on the work item -- without this check the
  // form would claim nothing is attached until the learner re-uploaded it.
  useEffect(() => {
    if (task.evidenceKind !== "attachment") return;
    let cancelled = false;
    void api<{ comments: Array<{ attachments: Array<{ fileName: string }> }> }>(`/api/tasks/${encodeURIComponent(task.workItemId)}/comments`)
      .then((result) => {
        if (cancelled) return;
        const withFile = result.comments.find((comment) => comment.attachments.length > 0);
        if (withFile) setAttachedFileName(withFile.attachments[0].fileName);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [task.evidenceKind, task.workItemId]);

  const uploadAttachment = async (file: File) => {
    setAttachmentBusy(true);
    setAttachmentError("");
    try {
      await uploadTaskAttachment(task.workItemId, file, asDoc(`Attached ${file.name} as evidence.`));
      setAttachedFileName(file.name);
    } catch (reason) {
      setAttachmentError(reason instanceof Error ? reason.message : "Could not upload the file.");
    } finally {
      setAttachmentBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setSubmitError(null);
    try {
      const normalizedUrl = normalizeUrl(url);
      const bodyByKind: Record<string, unknown> = {
        acknowledgement: { acknowledge: acknowledged },
        any: text ? { comment: asDoc(text) } : normalizedUrl ? { url: normalizedUrl } : { acknowledge: acknowledged },
        attachment: {},
        comment: { comment: asDoc(text) },
        link: { url: normalizedUrl },
        work_item: { evidenceWorkItemId: workItemId },
      };
      await api(`/api/onboarding/assignment-tasks/${task.id}/submit`, {
        method: "POST",
        body: JSON.stringify(bodyByKind[task.evidenceKind] ?? {}),
      });
      showToast(task.requiresApproval ? "Submitted for review." : "Task completed.", "success");
      onDone();
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "Could not submit.";
      setSubmitError(message);
      showToast(message, "error");
    } finally {
      setBusy(false);
    }
  };

  const taskHref = `/teams/${encodeURIComponent(teamId)}/tasks/${encodeURIComponent(task.workItemId)}`;
  const trimmed = text.trim();
  const minChars = task.evidenceMinChars ?? 0;
  const shortBy = minChars - trimmed.length;

  // Mirror the server's own rules (EVIDENCE_REQUIRED / EVIDENCE_TOO_SHORT in
  // onboarding-api.ts) so the learner is never invited to press a button that
  // was always going to fail -- writing 300 words and being told afterwards is
  // the worst version of this interaction.
  const blockedReason = ((): string | null => {
    if (task.evidenceKind === "comment" || (task.evidenceKind === "any" && trimmed)) {
      if (!trimmed) return "Write your evidence before submitting.";
      if (shortBy > 0) return `${shortBy} more character${shortBy === 1 ? "" : "s"} needed.`;
      return null;
    }
    if (task.evidenceKind === "link" || (task.evidenceKind === "any" && url)) {
      if (!url.trim()) return "Add a link before submitting.";
      return normalizeUrl(url) ? null : "That link doesn't look right.";
    }
    if (task.evidenceKind === "work_item") return workItemId.trim() ? null : "Enter the work item id to link.";
    if (task.evidenceKind === "attachment") return attachedFileName ? null : "Attach a file before submitting.";
    if (task.evidenceKind === "acknowledgement") return acknowledged ? null : "Tick the confirmation box first.";
    if (task.evidenceKind === "any") return acknowledged ? null : "Write evidence, add a link, or tick the box.";
    return null;
  })();

  return (
    <div className="mt-2 border-top pt-2">
      <label className="form-label small fw-semibold mb-1" htmlFor={`evidence-${task.id}`}>
        {EVIDENCE_LABEL[task.evidenceKind]}
      </label>
      {task.evidencePrompt && <p className="text-body-secondary small mb-2">{task.evidencePrompt}</p>}

      {(task.evidenceKind === "comment" || task.evidenceKind === "any") && (
        <textarea
          className="form-control form-control-sm mb-1"
          id={`evidence-${task.id}`}
          rows={5}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="Your written evidence"
        />
      )}
      {(task.evidenceKind === "comment" || task.evidenceKind === "any") && minChars > 0 && (
        <p className={`small mb-2 ${shortBy <= 0 ? "text-success" : "text-body-secondary"}`}>
          {trimmed.length} / {minChars} characters
          {shortBy > 0 ? ` — ${shortBy} to go` : " — long enough"}
        </p>
      )}
      {(task.evidenceKind === "link" || task.evidenceKind === "any") && (
        <input
          className="form-control form-control-sm mb-2"
          id={task.evidenceKind === "link" ? `evidence-${task.id}` : undefined}
          type="url"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="https://…"
        />
      )}
      {task.evidenceKind === "work_item" && (
        <input
          className="form-control form-control-sm mb-2"
          id={`evidence-${task.id}`}
          value={workItemId}
          onChange={(event) => setWorkItemId(event.target.value)}
          placeholder="Task key (e.g. STO-1637), task link or id"
        />
      )}
      {task.evidenceKind === "attachment" && (
        <div className="mb-2">
          <input
            className="form-control form-control-sm"
            id={`evidence-${task.id}`}
            type="file"
            disabled={attachmentBusy}
            onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadAttachment(file); }}
          />
          {attachmentBusy && <p className="small text-body-secondary mt-1 mb-0">Uploading…</p>}
          {!attachmentBusy && attachedFileName && <p className="small text-success mt-1 mb-0">Attached: {attachedFileName}</p>}
          {attachmentError && <p className="small text-danger mt-1 mb-0">{attachmentError}</p>}
          <p className="small text-body-secondary mt-1 mb-0">
            Or <a href={taskHref} target="_blank" rel="noreferrer">open this task ↗</a> to attach it there instead.
          </p>
        </div>
      )}
      {(task.evidenceKind === "acknowledgement" || task.evidenceKind === "any") && (
        <div className="form-check mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            id={`ack-${task.id}`}
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
          />
          <label className="form-check-label small" htmlFor={`ack-${task.id}`}>
            I confirm I have completed this task.
          </label>
        </div>
      )}

      <div className="d-flex align-items-center gap-2 flex-wrap">
        <button className="btn btn-sm btn-primary" disabled={busy || Boolean(blockedReason)} onClick={() => void submit()}>
          {task.requiresApproval ? "Submit for review" : "Mark complete"}
        </button>
        {blockedReason && <span className="small text-body-secondary">{blockedReason}</span>}
        {!blockedReason && submitError && <span className="small text-danger" role="alert">{submitError}</span>}
        {!blockedReason && task.requiresApproval && (
          <span className="small text-body-secondary">Your mentor reviews this before it counts.</span>
        )}
      </div>
      {task.state === "rejected" && task.reviewNote && (
        <p className="small text-danger mt-2 mb-0">Mentor note: {task.reviewNote}</p>
      )}
    </div>
  );
}

const plainText = (doc: unknown): string => {
  const walk = (node: { text?: string; content?: unknown[] } | undefined): string => {
    if (!node) return "";
    if (node.text) return node.text;
    return (node.content ?? []).map((child) => walk(child as never)).join("");
  };
  return walk(doc as never);
};

// plainText above runs paragraphs together, which is fine for a one-line quiz
// question and wrong for re-filling a textarea with several paragraphs of
// someone's writing -- their structure would silently collapse on resubmit.
const docToEditableText = (doc: unknown): string => {
  const blocks = (doc as { content?: unknown[] } | null)?.content;
  if (!Array.isArray(blocks)) return "";
  return blocks.map((block) => plainText(block)).join("\n\n").trim();
};

function QuizAttempt({ assignmentId, quiz, onDone }: { assignmentId: string; quiz: QuizState; onDone: () => void }) {
  const [attempt, setAttempt] = useState<{ attemptId: string; questions: QuizQuestion[] } | null>(null);
  const [answers, setAnswers] = useState<Record<string, { optionIds: string[]; text: string }>>({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ outcome: string; scorePct: number | null } | null>(null);
  const showToast = useToast();
  const confirm = useConfirm();

  const start = async () => {
    // Starting an attempt is not free: it consumes one of a bounded number and
    // may reopen a whole stage on failure. Say so before it is spent.
    const attemptsLeft = quiz.maxAttempts == null ? null : quiz.maxAttempts - quiz.attemptsUsed;
    const stakes = [
      `You need ${quiz.passThresholdPct}% to pass.`,
      attemptsLeft == null ? "Attempts are unlimited." : `This uses 1 of your ${attemptsLeft} remaining attempt${attemptsLeft === 1 ? "" : "s"}.`,
      quiz.onFail === "retry_quiz"
        ? null
        : `If you do not pass, ${ON_FAIL_CONSEQUENCE[quiz.onFail] ?? "some completed work reopens"}.`,
      quiz.cooldownHours > 0 ? `A failed attempt locks retakes for ${quiz.cooldownHours} hours.` : null,
    ].filter(Boolean).join(" ");
    if (!(await confirm({
      title: `Start “${quiz.title}”?`,
      description: stakes,
      confirmLabel: "Start the quiz",
      tone: quiz.onFail === "retry_quiz" ? "default" : "danger",
    }))) return;

    setBusy(true);
    try {
      const started = await api<{ attemptId: string; questions: QuizQuestion[] }>(
        `/api/onboarding/assignments/${encodeURIComponent(assignmentId)}/quizzes/${encodeURIComponent(quiz.id)}/attempts`,
        { method: "POST" },
      );
      setAttempt(started);
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not start the quiz.", "error");
    } finally {
      setBusy(false);
    }
  };

  const answeredCount = attempt
    ? attempt.questions.filter((question) => {
        const answer = answers[question.id];
        return question.kind === "short_text" ? Boolean(answer?.text.trim()) : Boolean(answer?.optionIds.length);
      }).length
    : 0;

  const submit = async () => {
    if (!attempt) return;
    const unanswered = attempt.questions.length - answeredCount;
    // Submitting blind burns an attempt and, on a resetting quiz, a stage.
    if (unanswered > 0 && !(await confirm({
      title: `Submit with ${unanswered} unanswered?`,
      description: `An unanswered question scores zero. You need ${quiz.passThresholdPct}% to pass and this submission is final.`,
      confirmLabel: "Submit anyway",
      tone: "danger",
    }))) return;

    setBusy(true);
    try {
      const payload = attempt.questions.map((question) => ({
        questionId: question.id,
        selectedOptionIds: answers[question.id]?.optionIds ?? [],
        textAnswer: answers[question.id]?.text ?? null,
      }));
      const result = await api<{ outcome: string; scorePct: number | null }>(
        `/api/onboarding/quiz-attempts/${attempt.attemptId}/submit`,
        { method: "POST", body: JSON.stringify({ answers: payload }) },
      );
      showToast(
        result.outcome === "passed" ? `Passed (${result.scorePct}%).`
        : result.outcome === "failed" ? `Did not pass (${result.scorePct}%).`
        : "Submitted — your mentor will grade the written answer.",
        result.outcome === "failed" ? "error" : "success",
      );
      // Keep the outcome on screen: a toast alone is easy to miss, and a failed
      // attempt starts the retake cooldown, so the learner must see that.
      setResult(result);
      setAttempt(null);
      setAnswers({});
      onDone();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not submit the quiz.", "error");
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    const failed = result.outcome === "failed";
    return (
      <div className={`alert ${failed ? "alert-danger" : "alert-success"} mt-2 mb-0`} role="status">
        <p className="mb-1 fw-semibold">
          {result.outcome === "passed" ? `Passed with ${result.scorePct}%.`
          : failed ? `Did not pass: ${result.scorePct}% (${quiz.passThresholdPct}% needed).`
          : "Submitted. Your mentor will grade the written answer."}
        </p>
        {failed && (
          <p className="small mb-1">
            {quiz.cooldownHours > 0 ? `You can retake this quiz in ${quiz.cooldownHours} hours.` : "You can retake this quiz now."}
          </p>
        )}
        <button className="btn btn-sm btn-outline-secondary" onClick={() => setResult(null)}>Dismiss</button>
      </div>
    );
  }

  if (!attempt) {
    return (
      <button className="btn btn-sm btn-primary mt-2" disabled={busy || !quiz.canStart} onClick={() => void start()}>
        {quiz.canStart ? "Start quiz" : "Locked"}
      </button>
    );
  }

  const setOption = (question: QuizQuestion, optionId: string) => {
    setAnswers((current) => {
      const existing = current[question.id]?.optionIds ?? [];
      const optionIds = question.kind === "multi_choice"
        ? existing.includes(optionId) ? existing.filter((id) => id !== optionId) : [...existing, optionId]
        : [optionId];
      return { ...current, [question.id]: { optionIds, text: current[question.id]?.text ?? "" } };
    });
  };

  return (
    <div className="border rounded p-3 mt-2">
      <p className="small text-body-secondary">
        {answeredCount} of {attempt.questions.length} answered · {quiz.passThresholdPct}% to pass
      </p>
      {attempt.questions.map((question, index) => (
        <fieldset className="mb-3" key={question.id}>
          <legend className="fs-6 fw-semibold">
            {index + 1}. {plainText(question.body)}
            {question.kind === "multi_choice" && (
              <span className="badge text-bg-light ms-2 fw-normal">select all that apply</span>
            )}
          </legend>
          {question.kind === "short_text" ? (
            <textarea
              className="form-control form-control-sm"
              rows={3}
              value={answers[question.id]?.text ?? ""}
              onChange={(event) => setAnswers((current) => ({ ...current, [question.id]: { optionIds: [], text: event.target.value } }))}
            />
          ) : question.options.map((option) => (
            <div className="form-check" key={option.id}>
              <input
                className="form-check-input"
                type={question.kind === "multi_choice" ? "checkbox" : "radio"}
                name={`q-${question.id}`}
                id={`opt-${option.id}`}
                checked={(answers[question.id]?.optionIds ?? []).includes(option.id)}
                onChange={() => setOption(question, option.id)}
              />
              <label className="form-check-label" htmlFor={`opt-${option.id}`}>{option.body}</label>
            </div>
          ))}
        </fieldset>
      ))}
      <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => void submit()}>Submit quiz</button>
    </div>
  );
}

function QuizPanel({ assignmentId, quizzes, onChanged }: { assignmentId: string; quizzes: QuizState[]; onChanged: () => void }) {
  const visible = quizzes.filter((quiz) => quiz.gateStatus !== "not_triggered" || quiz.trigger === "manual");
  if (!visible.length) return null;
  return (
    <div className="card mb-3 border-warning">
      <div className="card-header fw-semibold">Checkpoint quizzes</div>
      <ul className="list-group list-group-flush">
        {visible.map((quiz) => (
          <li className="list-group-item" key={quiz.key}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <span>
                <span className={`badge me-2 ${quiz.gateStatus === "passed" ? "text-bg-success" : quiz.gateStatus === "armed" ? "text-bg-warning" : "text-bg-secondary"}`}>
                  {quiz.gateStatus === "passed" ? "Passed" : quiz.gateStatus === "armed" ? "Ready" : "Not yet"}
                </span>
                <span className="fw-semibold">{quiz.title}</span>
                <span className="text-body-secondary small ms-2">
                  {quiz.questionCount} question{quiz.questionCount === 1 ? "" : "s"} · pass ≥ {quiz.passThresholdPct}%
                  {quiz.maxAttempts ? ` · ${quiz.attemptsUsed}/${quiz.maxAttempts} attempts used` : ""}
                </span>
              </span>
              {quiz.gateStatus === "armed" && quiz.lastOutcome === "awaiting_grading" && (
                <span className="small text-body-secondary">Awaiting mentor grading</span>
              )}
            </div>
            {quiz.gateStatus === "armed" && quiz.intro && (
              <RichTextView className="small text-body-secondary mt-2" doc={quiz.intro} />
            )}
            {quiz.gateStatus === "armed" && quiz.onFail !== "retry_quiz" && (
              <p className="small text-danger mt-2 mb-0">
                If you do not pass, {ON_FAIL_CONSEQUENCE[quiz.onFail] ?? "some completed work reopens"}.
              </p>
            )}
            {quiz.gateStatus === "armed" && quiz.lastOutcome !== "awaiting_grading" && (
              <QuizAttempt assignmentId={assignmentId} quiz={quiz} onDone={onChanged} />
            )}
            {quiz.gateStatus === "armed" && !quiz.canStart && quiz.retryAfter && (
              <p className="small text-body-secondary mt-1 mb-0">Retake available after {formatServerDateTime(quiz.retryAfter)}.</p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// The iframe src is never the stored URL -- toVideoEmbed re-derives it from a
// re-validated id (see src/video-embed.ts). An unrecognised URL renders as a
// plain link instead of a player, so a bad row degrades rather than breaking
// the page. `allow` deliberately omits autoplay: a video that starts talking
// the moment a stage expands is hostile, especially on a shared desk.
function VideoPlayer({ title, url }: { title: string | null; url: string }) {
  const embed = toVideoEmbed(url);
  if (!embed) {
    return (
      <p className="small mb-0">
        <a href={url} target="_blank" rel="noreferrer">{title ?? "Watch the video"} ↗</a>
      </p>
    );
  }
  return (
    <figure className="onboarding-video mb-0">
      <div className="onboarding-video-frame">
        <iframe
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          src={embed.embedUrl}
          title={title ?? "Course video"}
        />
      </div>
      <figcaption className="small text-body-secondary mt-2 d-flex justify-content-between gap-2 flex-wrap">
        <span>{title}</span>
        <a href={embed.watchUrl} target="_blank" rel="noreferrer">Open on {embed.provider === "youtube" ? "YouTube" : "Vimeo"} ↗</a>
      </figcaption>
    </figure>
  );
}

// The onboarding view previously had no way to ask a question about a task
// without leaving the plan for the linked work item's own comment thread --
// this reuses that exact thread (same work_item_id) so nothing is duplicated,
// it's just surfaced where the learner already is.
function TaskDiscussion({ otherPartyId, otherPartyName, workItemId }: {
  otherPartyId: string; otherPartyName: string; workItemId: string;
}) {
  const [comments, setComments] = useState<DiscussionComment[] | null>(null);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const showToast = useToast();

  const load = useCallback(() => {
    void api<{ comments: DiscussionComment[] }>(`/api/tasks/${encodeURIComponent(workItemId)}/comments`)
      .then((result) => setComments(result.comments))
      .catch((reason) => setError(reason instanceof Error ? reason.message : "Could not load the discussion."));
  }, [workItemId]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await api(`/api/tasks/${encodeURIComponent(workItemId)}/comments`, {
        method: "POST",
        body: JSON.stringify({ attachments: [], body: mentionDoc(otherPartyId, otherPartyName, trimmed) }),
      });
      setDraft("");
      load();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not send that.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="onboarding-discussion mt-3">
      <summary className="small fw-semibold">Discussion{comments?.length ? ` (${comments.length})` : ""}</summary>
      <div className="mt-2">
        {error && <p className="small text-danger">{error}</p>}
        {comments?.length === 0 && <p className="small text-body-secondary">Nothing here yet -- ask {otherPartyName} a question or leave a note.</p>}
        {comments?.map((comment) => (
          <div className="small border-start ps-2 mb-2" key={comment.id}>
            <div className="text-body-secondary">
              <span className="fw-semibold">{comment.authorName}</span> · {formatServerDateTime(comment.createdAt)}
            </div>
            <RichTextView doc={comment.body} />
            {comment.attachments.map((attachment) => <div className="text-body-secondary" key={attachment.id}>📎 {attachment.fileName}</div>)}
          </div>
        ))}
        <div className="d-flex gap-2 mt-2">
          <input
            className="form-control form-control-sm"
            aria-label={`Message ${otherPartyName}`}
            placeholder={`Message ${otherPartyName}…`}
            value={draft}
            disabled={busy}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); void send(); } }}
          />
          <button className="btn btn-sm btn-outline-secondary" disabled={busy || !draft.trim()} onClick={() => void send()}>Send</button>
        </div>
      </div>
    </details>
  );
}

// Focus mode: one unit, nothing else on screen. The plan view is a long list by
// necessity -- good for orientation, bad for actually learning something. When
// a task carries teaching material (a video, a body, or both) the learner can
// open it here, where the video is large, the prose is capped at a readable
// measure, and the only control is the one that finishes the unit.
function TaskStudyView({ task, teamId, siblings, onClose, onNavigate, onChanged, otherPartyId, otherPartyName }: {
  task: AssignmentTask;
  teamId: string;
  siblings: AssignmentTask[];
  onClose: () => void;
  onNavigate: (task: AssignmentTask) => void;
  onChanged: () => void;
  otherPartyId: string;
  otherPartyName: string;
}) {
  const index = siblings.findIndex((sibling) => sibling.id === task.id);
  const previous = index > 0 ? siblings[index - 1] : null;
  const next = index >= 0 && index < siblings.length - 1 ? siblings[index + 1] : null;
  const due = dueMeta(task.dueOn);
  const actionable = task.state === "open" || task.state === "rejected";

  // Escape is the gesture people already expect from a focused reading view.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <section className="onboarding-study" aria-label={`Studying: ${task.title}`}>
      <div className="onboarding-study-bar">
        <button className="btn btn-sm btn-link p-0" onClick={onClose}>← Back to plan</button>
        {index >= 0 && <span className="small text-body-secondary">{index + 1} of {siblings.length}</span>}
      </div>

      <div className="onboarding-study-body">
        <header className="mb-3">
          <h2 className="h4 mb-1">{task.title}</h2>
          <p className="small text-body-secondary mb-0">
            <span className={`badge me-2 ${STATE_BADGE[task.state].className}`}>{STATE_BADGE[task.state].label}</span>
            {task.requiresApproval && <span className="badge text-bg-light me-2">needs approval</span>}
            {due && <span className={due.className}>{due.label}</span>}
          </p>
        </header>

        {task.videoUrl && (
          <div className="mb-4">
            <p className="onboarding-study-eyebrow">Watch this before you answer</p>
            <VideoPlayer title={task.videoTitle} url={task.videoUrl} />
          </div>
        )}

        {task.body && <RichTextView className="onboarding-study-prose" doc={task.body} />}

        {actionable && (
          <div className="onboarding-study-evidence">
            <EvidenceForm task={task} teamId={teamId} onDone={onChanged} />
          </div>
        )}
        {task.state === "submitted" && (
          <p className="small text-body-secondary mt-3 mb-0">Submitted — waiting for your mentor to review.</p>
        )}
        {task.state === "approved" && (
          <p className="small text-success mt-3 mb-0">Approved. Nothing more to do here.</p>
        )}
        {task.state === "locked" && (
          <p className="small text-body-secondary mt-3 mb-0">
            This unit is locked
            {task.prerequisiteKeys.length > 0 ? " until its earlier units are approved." : "."}
          </p>
        )}

        {task.state !== "locked" && (
          <TaskDiscussion otherPartyId={otherPartyId} otherPartyName={otherPartyName} workItemId={task.workItemId} />
        )}

        <nav className="onboarding-study-nav">
          <button className="btn btn-sm btn-outline-secondary" disabled={!previous} onClick={() => previous && onNavigate(previous)}>
            ← {previous ? previous.title : "Previous"}
          </button>
          <button className="btn btn-sm btn-outline-secondary" disabled={!next} onClick={() => next && onNavigate(next)}>
            {next ? next.title : "Next"} →
          </button>
        </nav>
      </div>
    </section>
  );
}

function TaskRow({ task, teamId, titleByKey, onChanged, onStudy }: {
  task: AssignmentTask; teamId: string; titleByKey: Map<string, string>; onChanged: () => void;
  onStudy: (task: AssignmentTask) => void;
}) {
  const due = dueMeta(task.dueOn);
  const actionable = task.state === "open" || task.state === "rejected";
  // Anything carrying teaching material -- a talk, written guidance, or both --
  // is a lesson and belongs in focus mode. Keying this off the video alone made
  // one course render as two different products: units with a talk collapsed to
  // a tidy row while units with the same prose but no talk unrolled a full
  // evidence form inline. Tasks with nothing to read stay inline, where a single
  // checkbox or field is genuinely faster than a second screen.
  const isLesson = Boolean(task.videoUrl || task.body);

  // An approved task collapses to one line: the plan is long, and finished
  // work competing for space with remaining work is what made the original
  // page get harder to read the further you got through it.
  if (task.state === "approved") {
    return (
      <li className="list-group-item onboarding-task py-2" id={`task-${task.id}`}>
        <span className="badge me-2 text-bg-success">Approved</span>
        <span className="text-body-secondary text-decoration-line-through">{task.title}</span>
        {task.recurrence === "weekly" && <span className="badge text-bg-light ms-2">week {task.occurrence}</span>}
        {isLesson && (
          <button className="btn btn-sm btn-link p-0 ms-2" onClick={() => onStudy(task)}>
            {task.videoUrl ? "Rewatch" : "Review"}
          </button>
        )}
        <span className="ms-2"><TrackingTaskAction onTracked={onChanged} task={task} teamId={teamId} /></span>
      </li>
    );
  }

  return (
    <li className="list-group-item onboarding-task" id={`task-${task.id}`}>
      <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
        <div>
          <span className={`badge me-2 ${STATE_BADGE[task.state].className}`}>{STATE_BADGE[task.state].label}</span>
          <span className="fw-semibold">{task.title}</span>
          {task.recurrence === "weekly" && <span className="badge text-bg-light ms-2">week {task.occurrence}</span>}
          {task.requiresApproval && <span className="badge text-bg-light ms-2">needs approval</span>}
          {due && <span className={`small ms-2 ${due.className}`}>{due.label}</span>}
          {task.state === "locked" && task.prerequisiteKeys.length > 0 && (
            <div className="small text-body-secondary mt-1">
              Unlocks once these are approved: {task.prerequisiteKeys.map((key) => titleByKey.get(key) ?? key).join("; ")}
            </div>
          )}
        </div>
        {isLesson && (
          <button className="btn btn-sm btn-primary" onClick={() => onStudy(task)}>
            {task.state === "submitted"
              ? "Review"
              : task.videoUrl ? "Start lesson" : "Open"}
          </button>
        )}
      </div>
      {task.videoUrl && (
        <p className="small text-body-secondary mt-1 mb-0">
          <i aria-hidden="true" className="bi bi-play-btn me-1" />
          Includes a video{task.videoTitle ? `: ${task.videoTitle}` : ""}
        </p>
      )}
      {/* No inline body disclosure any more: every task carrying one is a
          lesson, and its guidance is shown in full in the focus view rather
          than hidden behind a "Why this matters" link most people never
          clicked. */}
      {actionable && !isLesson && <EvidenceForm task={task} teamId={teamId} onDone={onChanged} />}
      {task.state === "submitted" && (
        <p className="small text-body-secondary mt-2 mb-0">Submitted — waiting for your mentor to review.</p>
      )}
      <p className="mt-2 mb-0"><TrackingTaskAction onTracked={onChanged} task={task} teamId={teamId} /></p>
    </li>
  );
}

// Mirrors blockedStageIdsFor + the buildOpenPredicate reachability rule on
// the server, so a locked stage can say why it is locked instead of showing
// a wall of "Locked" badges with no explanation. Every stage -- "Track" or
// "Phase" alike -- gates on every EARLIER stage by `position` now (2026-09
// onboarding UX change: `kind` no longer carves Tracks out of this check).
// Narrowly typed (not the full AssignmentStage/QuizState shapes) and
// exported so it's unit-testable without a DOM (this repo's unit tests run
// under environment: "node", no jsdom) -- same convention as the pure
// functions in onboarding-api.ts.
export const stageLockReason = (
  stage: { position: number; tasks: Array<{ recurrence: "none" | "weekly"; state: string }>; title: string },
  stages: Array<{ id: string; position: number; tasks: Array<{ recurrence: "none" | "weekly"; state: string }>; title: string }>,
  quizzes: Array<{ gateStatus: string; stageId: string | null; title: string }>,
): string | null => {
  if (!stage.tasks.length || stage.tasks.some((task) => task.state !== "locked")) return null;
  const positionById = new Map(stages.map((entry) => [entry.id, entry.position]));
  const gate = quizzes.find((quiz) =>
    quiz.gateStatus === "armed" && quiz.stageId != null
    && (positionById.get(quiz.stageId) ?? Number.POSITIVE_INFINITY) < stage.position);
  if (gate) return `Held by the checkpoint “${gate.title}” — pass it to unlock this stage.`;
  const blocker = stages
    .filter((entry) => entry.position < stage.position)
    .sort((a, b) => a.position - b.position)
    .find((entry) => entry.tasks.some((task) => task.recurrence !== "weekly" && task.state !== "approved"));
  if (blocker) return `Unlocks when every task in “${blocker.title}” is approved.`;
  return null;
};

// Whether the learner has ever engaged with this plan at all -- decides
// whether the single call-to-action reads "Start" or "Continue". Exported
// and pure for the same reason as stageLockReason above.
export const hasEngagedAssignment = (
  approvedCount: number,
  stages: Array<{ tasks: Array<{ state: string }> }>,
): boolean =>
  approvedCount > 0 || stages.some((stage) => stage.tasks.some((task) => task.state === "submitted" || task.state === "rejected"));

function StageCard({ stage, stages, quizzes, teamId, titleByKey, open, onToggle, onChanged, onStudy }: {
  stage: AssignmentStage; stages: AssignmentStage[]; quizzes: QuizState[]; teamId: string;
  titleByKey: Map<string, string>; open: boolean; onToggle: () => void; onChanged: () => void;
  onStudy: (task: AssignmentTask) => void;
}) {
  const approved = stage.tasks.filter((task) => task.state === "approved").length;
  const lockReason = stageLockReason(stage, stages, quizzes);
  const actionable = stage.tasks.filter((task) => task.state === "open" || task.state === "rejected").length;

  return (
    <div className="card mb-3">
      <div className="card-header d-flex justify-content-between align-items-center gap-2 flex-wrap">
        <button
          className="btn btn-link p-0 text-start text-decoration-none fw-semibold"
          aria-expanded={open}
          onClick={onToggle}
        >
          <span aria-hidden="true" className="me-1">{open ? "▾" : "▸"}</span>
          {stage.title}
        </button>
        <span className="d-flex align-items-center gap-2">
          {actionable > 0 && <span className="badge text-bg-primary">{actionable} to do</span>}
          <span className="text-body-secondary small">{approved}/{stage.tasks.length} approved</span>
          <span className="badge text-bg-light">{stage.kind === "parallel" ? "Track" : "Phase"}</span>
        </span>
      </div>
      {/* Outside the collapse: a locked stage is collapsed by default, and a
          stage that is shut with no stated reason is the thing this replaces. */}
      {lockReason && (
        <div className="card-body py-2 border-bottom">
          <p className="small text-body-secondary mb-0"><span className="me-1">🔒</span>{lockReason}</p>
        </div>
      )}
      {open && (
        <>
          {stage.description && (
            <div className="card-body py-2 border-bottom">
              <RichTextView className="small text-body-secondary" doc={stage.description} />
            </div>
          )}
          <ul className="list-group list-group-flush">
            {stage.tasks.map((task) => (
              <TaskRow key={task.id} task={task} teamId={teamId} titleByKey={titleByKey} onChanged={onChanged} onStudy={onStudy} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function LearnerAssignment({ detail, onChanged }: { detail: AssignmentDetail; onChanged: () => void }) {
  const { assignment, quizzes, stages } = detail;
  const [openById, setOpenById] = useState<Record<string, boolean>>({});
  const percent = assignment.taskCount ? Math.round((assignment.approvedCount / assignment.taskCount) * 100) : 0;

  // Every stage -- "Track" or "Phase" alike -- sits in one single ordered
  // sequence by `position` (2026-09 onboarding UX change: a Track used to be
  // reachable from the start regardless of position and was listed after
  // every Phase for that reason; `kind` is now purely a display label, not a
  // gating rule, so display order matches gating order too).
  const ordered = useMemo(() => [...stages].sort((a, b) => a.position - b.position), [stages]);

  const titleByKey = useMemo(
    () => new Map(stages.flatMap((stage) => stage.tasks.map((task) => [task.key, task.title] as const))),
    [stages],
  );

  const armedQuiz = quizzes.find((quiz) => quiz.gateStatus === "armed" && quiz.canStart);

  // Focus mode. Held as an id, not the task object, so a refetch after
  // submitting evidence re-reads the fresh copy (new state, new review note)
  // instead of leaving a stale one on screen.
  const [studyTaskId, setStudyTaskId] = useState<string | null>(null);
  const studying = useMemo(() => {
    if (!studyTaskId) return null;
    for (const stage of ordered) {
      const task = stage.tasks.find((entry) => entry.id === studyTaskId);
      if (task) return { siblings: stage.tasks, stage, task };
    }
    return null;
  }, [ordered, studyTaskId]);

  // The single most important thing this page can answer is "what do I do
  // next". Without it a 31-task plan renders as an undifferentiated wall.
  const upNext = useMemo(() => ordered
    .flatMap((stage) => stage.tasks.map((task) => ({ stage, task })))
    .filter(({ task }) => task.state === "open" || task.state === "rejected")
    .sort((a, b) => {
      if ((a.task.state === "rejected") !== (b.task.state === "rejected")) return a.task.state === "rejected" ? -1 : 1;
      if (a.task.dueOn !== b.task.dueOn) return (a.task.dueOn ?? "9999").localeCompare(b.task.dueOn ?? "9999");
      return a.stage.position - b.stage.position;
    })
    .slice(0, 4), [ordered]);

  const hasStarted = hasEngagedAssignment(assignment.approvedCount, stages);

  // A stage opens by default when it holds live work. That default is then
  // frozen: recomputing it on every refetch would collapse a stage the moment
  // its last task is approved, yanking the section away from under the learner
  // who just acted in it.
  const defaultOpen = (stage: AssignmentStage) =>
    stage.tasks.some((task) => task.state === "open" || task.state === "rejected" || task.state === "submitted");
  const isOpen = (stage: AssignmentStage) => openById[stage.id] ?? defaultOpen(stage);

  useEffect(() => {
    setOpenById((current) => {
      const next = { ...current };
      let changed = false;
      for (const stage of stages) {
        if (!(stage.id in next)) { next[stage.id] = defaultOpen(stage); changed = true; }
      }
      return changed ? next : current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages]);

  const focusTask = (stageId: string, taskId: string) => {
    setOpenById((current) => ({ ...current, [stageId]: true }));
    requestAnimationFrame(() => {
      document.getElementById(`task-${taskId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  // Focus mode replaces the plan rather than overlaying it: the whole point is
  // that nothing else is on screen while the learner is working through a unit.
  if (studying) {
    return (
      <TaskStudyView
        task={studying.task}
        teamId={assignment.teamId}
        siblings={studying.siblings}
        onClose={() => setStudyTaskId(null)}
        onNavigate={(next) => setStudyTaskId(next.id)}
        onChanged={onChanged}
        otherPartyId={assignment.mentorUserId}
        otherPartyName={assignment.mentorName}
      />
    );
  }

  return (
    <section>
      <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2">
        <h2 className="h5 mb-0">{assignment.planTitle}</h2>
        <span className="text-body-secondary small">
          Mentor: {assignment.mentorName} · started {assignment.startedOn}
          {assignment.targetOn ? ` · target ${assignment.targetOn}` : ""}
        </span>
      </div>
      <div className="my-3">
        <OnboardingProgress stages={ordered} />
      </div>
      <p className="small text-body-secondary">
        {assignment.approvedCount} of {assignment.taskCount} tasks approved ({percent}%)
        {assignment.status === "completed" ? " · plan complete 🎉" : ""}
      </p>

      <QuizPanel assignmentId={assignment.id} quizzes={quizzes} onChanged={onChanged} />

      {armedQuiz ? (
        <div className="alert alert-warning py-2">
          <span className="fw-semibold">A checkpoint is waiting.</span>{" "}
          Pass “{armedQuiz.title}” to carry on with the gated stages.
        </div>
      ) : upNext.length > 0 ? (
        // A single, unambiguous next step -- not a growing list of every open
        // task across every currently-reachable stage (2026-09 onboarding UX
        // change: that list used to be the only way in, and with several
        // "Track" stages open at once it read as several unrelated entry
        // points rather than one plan). upNext is already sorted by priority
        // (a rejected task first, then by due date, then by stage position),
        // so its first entry IS the one thing to do next; anything else
        // still waiting is named, not hidden, in the caption below the button.
        <div className="card mb-3 border-primary-subtle onboarding-continue-card">
          <div className="card-body text-center py-4">
            <p className="onboarding-study-eyebrow mb-1">
              {hasStarted ? "Continue where you left off" : "Ready to begin"}
            </p>
            <h3 className="h5 mb-1">
              {upNext[0].task.state === "rejected" && <span className="badge text-bg-danger me-2">Changes requested</span>}
              {upNext[0].task.title}
            </h3>
            <p className="text-body-secondary small mb-3">
              {upNext[0].stage.title}
              {dueMeta(upNext[0].task.dueOn) && (
                <span className={`ms-2 ${dueMeta(upNext[0].task.dueOn)!.className}`}>{dueMeta(upNext[0].task.dueOn)!.label}</span>
              )}
            </p>
            <button
              className="btn btn-primary btn-lg px-4"
              onClick={() => (upNext[0].task.videoUrl || upNext[0].task.body
                ? setStudyTaskId(upNext[0].task.id)
                : focusTask(upNext[0].stage.id, upNext[0].task.id))}
            >
              {hasStarted ? "Continue" : "Start"}
            </button>
            {upNext.length > 1 && (
              <p className="small text-body-secondary mt-2 mb-0">+{upNext.length - 1} more waiting after this</p>
            )}
          </div>
        </div>
      ) : (
        <p className="text-body-secondary">
          Nothing is waiting on you right now — everything open has been submitted for review.
        </p>
      )}

      {ordered.map((stage) => (
        <StageCard
          key={stage.id}
          stage={stage}
          stages={ordered}
          quizzes={quizzes}
          teamId={assignment.teamId}
          titleByKey={titleByKey}
          open={isOpen(stage)}
          onToggle={() => setOpenById((current) => ({ ...current, [stage.id]: !isOpen(stage) }))}
          onChanged={onChanged}
          onStudy={(task) => setStudyTaskId(task.id)}
        />
      ))}
    </section>
  );
}

function QuizGrading({ attemptId, onDone }: { attemptId: string; onDone: () => void }) {
  const [view, setView] = useState<AttemptView | null>(null);
  const [grades, setGrades] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const showToast = useToast();

  useEffect(() => {
    void api<AttemptView>(`/api/onboarding/quiz-attempts/${attemptId}`)
      .then(setView)
      .catch((reason) => showToast(reason instanceof Error ? reason.message : "Could not load the attempt.", "error"));
  }, [attemptId, showToast]);

  if (!view) return <p className="small text-body-secondary">Loading attempt…</p>;
  const shortText = view.questions.filter((question) => question.kind === "short_text");

  const submitGrades = async () => {
    setBusy(true);
    try {
      const result = await api<{ outcome: string; scorePct: number | null }>(
        `/api/onboarding/quiz-attempts/${attemptId}/grade`,
        { method: "POST", body: JSON.stringify({ grades: shortText.map((question) => ({ questionId: question.id, isCorrect: Boolean(grades[question.id]) })) }) },
      );
      showToast(result.outcome === "passed" ? `Graded — learner passed (${result.scorePct}%).` : `Graded — learner did not pass (${result.scorePct}%).`, result.outcome === "passed" ? "success" : "info");
      onDone();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not save grades.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border rounded p-2 mt-2">
      {shortText.map((question) => (
        <div className="mb-2" key={question.id}>
          <p className="small fw-semibold mb-1">{plainText(question.body)} <span className="text-body-secondary">({question.pointsPossible} pt)</span></p>
          <blockquote className="small border-start ps-2 text-body-secondary">{question.textAnswer || <em>no answer</em>}</blockquote>
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id={`grade-${question.id}`} checked={Boolean(grades[question.id])} onChange={(event) => setGrades((current) => ({ ...current, [question.id]: event.target.checked }))} />
            <label className="form-check-label small" htmlFor={`grade-${question.id}`}>Correct</label>
          </div>
        </div>
      ))}
      <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => void submitGrades()}>Save grades</button>
    </div>
  );
}

function ReviewQueue({ details, onChanged }: { details: AssignmentDetail[]; onChanged: () => void }) {
  const showToast = useToast();
  const [noteById, setNoteById] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  // Oldest submission first -- otherwise a mentor working top-to-bottom never
  // actually reaches the one that's been sitting the longest.
  const pending = details.flatMap((detail) =>
    detail.stages.flatMap((stage) => stage.tasks
      .filter((task) => task.state === "submitted")
      .map((task) => ({ assignment: detail.assignment, stage, task }))))
    .sort((a, b) => new Date(a.task.submittedAt ?? 0).getTime() - new Date(b.task.submittedAt ?? 0).getTime());
  const staleCount = pending.filter(({ task }) => reviewAge(task.submittedAt)?.stale).length;
  const quizzesToGrade = details.flatMap((detail) =>
    detail.quizzes
      .filter((quiz) => quiz.gradableAttemptId)
      .map((quiz) => ({ assignment: detail.assignment, quiz })));

  const review = async (taskId: string, decision: "approve" | "reject") => {
    if (decision === "reject" && !(noteById[taskId] ?? "").trim()) {
      showToast("Add a note explaining what needs to change.", "error");
      return;
    }
    setBusyId(taskId);
    try {
      await api(`/api/onboarding/assignment-tasks/${taskId}/review`, {
        method: "POST",
        body: JSON.stringify({ decision, note: noteById[taskId] ?? null }),
      });
      showToast(decision === "approve" ? "Approved." : "Sent back to the learner.", "success");
      onChanged();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not record the review.", "error");
    } finally {
      setBusyId(null);
    }
  };

  if (!pending.length && !quizzesToGrade.length) return <p className="text-body-secondary">Nothing is waiting for your review.</p>;

  return (
    <section className="d-flex flex-column gap-3">
      {staleCount > 0 && (
        <div className="alert alert-danger py-2 mb-0">
          {staleCount} submission{staleCount === 1 ? " has" : "s have"} been waiting more than a business day.
        </div>
      )}
      {quizzesToGrade.map(({ assignment, quiz }) => (
        <div className="card border-warning" key={quiz.gradableAttemptId}>
          <div className="card-body">
            <span className="fw-semibold">Grade quiz: {quiz.title}</span>
            <div className="small text-body-secondary">{assignment.learnerName} · {assignment.planTitle}</div>
            <QuizGrading attemptId={quiz.gradableAttemptId!} onDone={onChanged} />
          </div>
        </div>
      ))}
      {pending.map(({ assignment, stage, task }) => {
        const age = reviewAge(task.submittedAt);
        return (
        <div className={`card ${age?.stale ? "border-danger" : ""}`} key={task.id}>
          <div className="card-body">
            <div className="d-flex justify-content-between flex-wrap gap-2">
              <div>
                <span className="fw-semibold">{task.title}</span>
                <div className="small text-body-secondary">
                  {assignment.learnerName} · {assignment.planTitle} · {stage.title}
                  {task.taskKey ? ` · ${task.taskKey}` : ""}
                  {age && <> · <span className={age.className}>{age.label}</span></>}
                </div>
              </div>
              <a className="small" href={`/teams/${encodeURIComponent(assignment.teamId)}/tasks/${encodeURIComponent(task.workItemId)}`} target="_blank" rel="noreferrer">
                Open task ↗
              </a>
            </div>
            {/* The bar the learner was given. Approving without it means every
                mentor invents their own standard. */}
            {task.evidencePrompt && (
              <p className="small text-body-secondary border-start ps-2 my-2">
                <span className="fw-semibold d-block">What was asked</span>
                {task.evidencePrompt}
              </p>
            )}
            {/* And what they actually wrote. Without this the mentor could see
                the question but not the answer, so every approval was either
                made blind or required opening the work item in another tab --
                on a 37-task plan, once per submission. */}
            {task.latestEvidence ? (
              <div className="small border-start border-primary-subtle ps-2 my-2">
                <span className="fw-semibold d-block">What they submitted</span>
                <RichTextView doc={task.latestEvidence} />
              </div>
            ) : (
              <p className="small text-body-secondary fst-italic my-2">
                No written answer on this task — open it to check the {task.evidenceKind === "attachment" ? "attached file" : "evidence"}.
              </p>
            )}
            <textarea
              className="form-control form-control-sm my-2"
              rows={2}
              aria-label={`Review note for ${task.title}`}
              placeholder="Note (required to send back)"
              value={noteById[task.id] ?? ""}
              onChange={(event) => setNoteById((current) => ({ ...current, [task.id]: event.target.value }))}
            />
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-success" disabled={busyId === task.id} onClick={() => void review(task.id, "approve")}>Approve</button>
              <button className="btn btn-sm btn-outline-danger" disabled={busyId === task.id} onClick={() => void review(task.id, "reject")}>Request changes</button>
            </div>
          </div>
        </div>
        );
      })}
    </section>
  );
}

type StageProgress = {
  key: string; title: string; position: number;
  total: number; approved: number; submitted: number; rejected: number; open: number;
};
type ProgressAssignment = AssignmentSummary & {
  mentorName: string; completedAt: string | null; stages: StageProgress[];
};

// Explodes the aggregated per-state counts back into the shape
// OnboardingProgress expects (one entry per task). It only reads `state`, so a
// repeated placeholder is enough and keeps a single tested renderer.
const stageBarsFor = (stages: StageProgress[]) =>
  stages.map((stage) => ({
    title: stage.title,
    tasks: ([
      ["approved", stage.approved], ["submitted", stage.submitted],
      ["rejected", stage.rejected], ["open", stage.open],
    ] as const).flatMap(([state, count]) => Array.from({ length: count }, () => ({ state }))),
  }));

// "Who is behind" for one row on the Team progress tab -- before this, the
// only way to notice a stalled learner was to open their assignment and read
// every task by hand. Only meaningful while the plan is still active: a
// completed or unassigned row has nothing left to be behind on.
const behindReasons = (assignment: ProgressAssignment): Array<{ className: string; label: string }> => {
  if (assignment.status !== "active") return [];
  const reasons: Array<{ className: string; label: string }> = [];
  if (assignment.overdueCount > 0) {
    reasons.push({ className: "text-bg-danger", label: `${assignment.overdueCount} overdue` });
  }
  if (assignment.oldestSubmittedAt && new Date(assignment.oldestSubmittedAt).getTime() < oneBusinessDayCutoff(new Date()).getTime()) {
    reasons.push({ className: "text-bg-warning", label: "review overdue" });
  }
  const inactiveDays = assignment.lastActivityAt
    ? Math.floor((Date.now() - new Date(assignment.lastActivityAt).getTime()) / 86_400_000)
    : null;
  if (inactiveDays !== null && inactiveDays >= 7) {
    reasons.push({ className: "text-bg-secondary", label: `inactive ${inactiveDays}d` });
  }
  return reasons;
};

// Every onboarding task's own work_item is deliberately hidden from the
// board, so "Open task" had nothing real to link to until a tracking task --
// a genuine second, ordinary work_item -- is created for it. Shared by the
// learner's own TaskRow and the mentor/admin/HR read-only ProgressTaskRow:
// both a learner organising their own sprint work and a mentor/admin/HR
// wanting a real link can create one.
function TrackingTaskAction({ task, teamId, onTracked }: {
  onTracked: (result: TrackingTaskResult) => void; task: AssignmentTask; teamId: string;
}) {
  const [busy, setBusy] = useState(false);
  const showToast = useToast();

  if (task.trackingWorkItemId) {
    return (
      <a className="small" href={trackingTaskHref(teamId, task.trackingWorkItemId, task.trackingSprintId)} target="_blank" rel="noreferrer">
        Open task ↗
      </a>
    );
  }
  const track = async () => {
    setBusy(true);
    try {
      onTracked(await createTrackingTask(task.id));
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not create the tracking task.", "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button className="btn btn-sm btn-link p-0 small" disabled={busy} onClick={() => void track()}>
      Create tracking task
    </button>
  );
}

// A single read-only task row for the Team progress drill-down -- distinct
// from TaskRow, which is the learner's own interactive row (evidence forms,
// "start lesson" buttons). A mentor/admin/HR viewer here can look but never
// act on the onboarding task itself, so this only ever renders state + what
// was submitted -- the one exception is creating a tracking task, since that
// affects the sprint board, not the onboarding task's own state. Progress
// alone (state, submission, evidence) doesn't say what the task actually
// *asks* of the learner -- "Task details" discloses the plan's own content
// for it (instructions, video, what evidence is expected), the same
// material TaskStudyView shows the learner, so a mentor/admin can judge a
// submission against the brief without leaving this tab.
function ProgressTaskRow({ task, teamId, onTracked }: {
  onTracked: (result: TrackingTaskResult) => void; task: AssignmentTask; teamId: string;
}) {
  const due = dueMeta(task.dueOn);
  const [showDetails, setShowDetails] = useState(false);
  const hasTaskContent = Boolean(task.body || task.videoUrl || task.evidencePrompt);

  return (
    <li className="list-group-item py-2">
      <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
        <div>
          <span className={`badge me-2 ${STATE_BADGE[task.state].className}`}>{STATE_BADGE[task.state].label}</span>
          <span className={task.state === "approved" ? "text-body-secondary text-decoration-line-through" : "fw-semibold"}>{task.title}</span>
          {task.recurrence === "weekly" && <span className="badge text-bg-light ms-2">week {task.occurrence}</span>}
          {due && task.state !== "approved" && <span className={`small ms-2 ${due.className}`}>{due.label}</span>}
        </div>
        <div className="d-flex gap-2 align-items-center">
          <button
            className="btn btn-sm btn-link p-0 small"
            aria-expanded={showDetails}
            onClick={() => setShowDetails((current) => !current)}
          >
            {showDetails ? "Hide task details" : "Task details"}
          </button>
          <TrackingTaskAction onTracked={onTracked} task={task} teamId={teamId} />
        </div>
      </div>
      {showDetails && (
        <div className="border-start ps-2 my-2">
          <p className="small text-body-secondary mb-2">{EVIDENCE_LABEL[task.evidenceKind]}</p>
          {task.videoUrl && <div className="mb-2"><VideoPlayer title={task.videoTitle} url={task.videoUrl} /></div>}
          {task.body && <RichTextView className="small" doc={task.body} />}
          {task.evidencePrompt && (
            <p className="small text-body-secondary mt-2 mb-0">
              <span className="fw-semibold d-block">What's asked</span>
              {task.evidencePrompt}
            </p>
          )}
          {!hasTaskContent && <p className="small text-body-secondary fst-italic mb-0">This task has no extra instructions beyond its title.</p>}
        </div>
      )}
      {task.submittedAt && (
        <p className="small text-body-secondary mt-1 mb-0">Submitted {task.submittedAt.slice(0, 10)}</p>
      )}
      {task.reviewNote && (
        <p className="small border-start border-danger-subtle ps-2 mt-1 mb-0">
          <span className="fw-semibold d-block">Review note</span>{task.reviewNote}
        </p>
      )}
      {task.latestEvidence && (
        <div className="small border-start border-primary-subtle ps-2 mt-1 mb-0">
          <span className="fw-semibold d-block">What they submitted</span>
          <RichTextView doc={task.latestEvidence} />
        </div>
      )}
    </li>
  );
}

// The actual per-task drill-down. Team progress's stage bars only ever
// carried aggregate counts; a mentor or admin who needs to know *which*
// task a learner is stuck on, or read what they wrote, had no way to see it
// without opening the review queue (which only surfaces tasks awaiting
// review) or the learner's own board tasks one at a time.
function AssignmentDetailView({ detail, onTaskTracked }: {
  detail: AssignmentDetail; onTaskTracked: (taskId: string, result: TrackingTaskResult) => void;
}) {
  const ordered = useMemo(() => [...detail.stages].sort((a, b) => a.position - b.position), [detail.stages]);
  return (
    <div className="mt-3 border-top pt-3">
      {detail.quizzes.length > 0 && (
        <div className="mb-3">
          <h3 className="h6">Checkpoints</h3>
          <ul className="list-group list-group-flush">
            {detail.quizzes.map((quiz) => (
              <li className="list-group-item d-flex justify-content-between align-items-center py-2" key={quiz.id}>
                <span>{quiz.title}</span>
                <span className={`badge ${quiz.gateStatus === "passed" ? "text-bg-success" : quiz.gateStatus === "armed" ? "text-bg-warning" : "text-bg-secondary"}`}>
                  {quiz.gateStatus === "passed" ? "Passed" : quiz.gateStatus === "armed" ? "Awaiting attempt" : "Not yet reached"}
                  {quiz.attemptsUsed > 0 ? ` · ${quiz.attemptsUsed} attempt${quiz.attemptsUsed === 1 ? "" : "s"}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {ordered.filter((stage) => stage.tasks.length > 0).map((stage) => (
        <div className="mb-3" key={stage.id}>
          <h3 className="h6 mb-1">{stage.title}</h3>
          <ul className="list-group list-group-flush">
            {stage.tasks.map((task) => (
              <ProgressTaskRow key={task.id} onTracked={(result) => onTaskTracked(task.id, result)} task={task} teamId={detail.assignment.teamId} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function TeamProgress({ teamId, canManage, userId }: { canManage: boolean; teamId: string; userId: string }) {
  const [assignments, setAssignments] = useState<ProgressAssignment[] | null>(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<"all" | "active" | "behind" | "completed">("all");
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailById, setDetailById] = useState<Record<string, AssignmentDetail | "error">>({});
  const showToast = useToast();
  const confirm = useConfirm();

  // getAssignment (the backing endpoint) only authorises the assignment's own
  // mentor-of-record or someone privileged on the team -- a mentor merely
  // sharing the Team progress list with a colleague's mentee would get a 403
  // reading it, so the "View details" affordance only appears where it will
  // actually work.
  const canViewDetail = (assignment: ProgressAssignment) => canManage || assignment.mentorUserId === userId;

  const toggleDetail = async (assignment: ProgressAssignment) => {
    if (expandedId === assignment.id) { setExpandedId(null); return; }
    setExpandedId(assignment.id);
    if (detailById[assignment.id]) return;
    try {
      const full = await api<AssignmentDetail>(`/api/onboarding/assignments/${encodeURIComponent(assignment.id)}`);
      setDetailById((current) => ({ ...current, [assignment.id]: full }));
    } catch {
      setDetailById((current) => ({ ...current, [assignment.id]: "error" }));
    }
  };

  // Patches the one task's tracking fields in place rather than refetching
  // the whole assignment -- the drill-down's expanded/collapsed stage state
  // shouldn't reset just because one task grew a tracking link.
  const patchTracked = (assignmentId: string, taskId: string, result: TrackingTaskResult) => {
    setDetailById((current) => {
      const detail = current[assignmentId];
      if (!detail || detail === "error") return current;
      return {
        ...current,
        [assignmentId]: {
          ...detail,
          stages: detail.stages.map((stage) => ({
            ...stage,
            tasks: stage.tasks.map((task) => (task.id === taskId
              ? { ...task, trackingSprintId: result.sprintId, trackingTaskKey: result.taskKey, trackingWorkItemId: result.workItemId }
              : task)),
          })),
        },
      };
    });
  };

  const reload = useCallback(() => {
    void api<{ assignments: ProgressAssignment[] }>(`/api/onboarding/progress?teamId=${encodeURIComponent(teamId)}`)
      .then((result) => setAssignments(result.assignments))
      .catch((reason) => setError(reason instanceof Error ? reason.message : "Could not load team progress."));
  }, [teamId]);

  useEffect(() => { reload(); }, [reload]);

  const unassign = async (assignment: ProgressAssignment) => {
    if (!(await confirm({
      title: "Unassign this plan?",
      description: `${assignment.learnerName} will stop seeing “${assignment.planTitle}” in My onboarding. Their progress is kept and stays visible here, and you can re-assign the plan later.`,
      confirmLabel: "Unassign",
      tone: "danger",
    }))) return;
    setBusyId(assignment.id);
    try {
      await api(`/api/onboarding/assignments/${encodeURIComponent(assignment.id)}/cancel`, { method: "POST" });
      showToast(`Unassigned ${assignment.learnerName} from “${assignment.planTitle}”.`, "success");
      reload();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not unassign the plan.", "error");
    } finally {
      setBusyId("");
    }
  };

  const remove = async (assignment: ProgressAssignment) => {
    if (!(await confirm({
      title: "Delete this assignment?",
      description: `“${assignment.planTitle}” for ${assignment.learnerName}, every task state and quiz attempt, and the ${assignment.taskCount} onboarding task${assignment.taskCount === 1 ? "" : "s"} on their board will be permanently deleted. This cannot be undone.`,
      confirmLabel: "Delete assignment",
      tone: "danger",
    }))) return;
    setBusyId(assignment.id);
    try {
      const result = await api<{ removedWorkItems: number }>(`/api/onboarding/assignments/${encodeURIComponent(assignment.id)}`, { method: "DELETE" });
      showToast(`Assignment deleted${result.removedWorkItems ? ` (${result.removedWorkItems} task${result.removedWorkItems === 1 ? "" : "s"} removed)` : ""}.`, "success");
      reload();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : "Could not delete the assignment.", "error");
      setBusyId("");
    }
  };

  if (error) return <ErrorBanner>{error}</ErrorBanner>;
  if (!assignments) return <Skeleton label="Loading team progress…" variant="list" />;

  const visible = assignments.filter((assignment) => {
    if (filter === "completed" && assignment.status !== "completed") return false;
    if (filter === "active" && (assignment.status === "completed" || assignment.status === "cancelled")) return false;
    if (filter === "behind" && behindReasons(assignment).length === 0) return false;
    const needle = query.trim().toLowerCase();
    if (needle && !`${assignment.learnerName} ${assignment.mentorName} ${assignment.planTitle}`.toLowerCase().includes(needle)) return false;
    return true;
  });
  const behindCount = assignments.filter((assignment) => behindReasons(assignment).length > 0).length;

  return (
    <section>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <div className="btn-group btn-group-sm" role="group" aria-label="Filter by status">
          {(["all", "active", "behind", "completed"] as const).map((value) => (
            <button key={value} type="button" className={`btn btn-outline-secondary ${filter === value ? "active" : ""}`} onClick={() => setFilter(value)}>
              {value === "all" ? "All" : value === "active" ? "In progress" : value === "completed" ? "Completed" : `Behind${behindCount ? ` (${behindCount})` : ""}`}
            </button>
          ))}
        </div>
        <input
          className="form-control form-control-sm w-auto flex-grow-1"
          placeholder="Filter by learner, mentor, or plan"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Filter team onboarding progress"
        />
      </div>

      {!assignments.length ? (
        <p className="text-body-secondary">No onboarding plans are assigned on this team yet.</p>
      ) : !visible.length ? (
        <p className="text-body-secondary">No assignments match the current filter.</p>
      ) : (
        <div className="d-flex flex-column gap-4">
          {visible.map((assignment) => {
            const pct = assignment.taskCount ? Math.round((assignment.approvedCount / assignment.taskCount) * 100) : 0;
            return (
              <article className="card" key={assignment.id}>
                <div className="card-body">
                  <div className="d-flex justify-content-between flex-wrap gap-2">
                    <div>
                      <span className="fw-semibold">{assignment.learnerName}</span>
                      <span className="text-body-secondary"> · {assignment.planTitle}</span>
                    </div>
                    <div className="d-flex gap-1 flex-wrap align-self-start">
                      {behindReasons(assignment).map((reason) => (
                        <span className={`badge ${reason.className}`} key={reason.label}>{reason.label}</span>
                      ))}
                      <span className={`badge ${
                        assignment.status === "completed" ? "text-bg-success"
                        : assignment.status === "cancelled" ? "text-bg-secondary"
                        : "text-bg-primary"
                      }`}>
                        {assignment.status === "completed" ? "Completed"
                          : assignment.status === "cancelled" ? "Unassigned"
                          : `${pct}% approved`}
                      </span>
                    </div>
                  </div>
                  <div className="small text-body-secondary mb-2">
                    Mentor: {assignment.mentorName} · started {assignment.startedOn}
                    {assignment.targetOn ? ` · target ${assignment.targetOn}` : ""}
                    {assignment.completedAt ? ` · completed ${assignment.completedAt.slice(0, 10)}` : ""}
                    {" · "}{assignment.approvedCount}/{assignment.taskCount} tasks approved
                    {assignment.lastActivityAt ? ` · last activity ${assignment.lastActivityAt.slice(0, 10)}` : ""}
                  </div>
                  {assignment.stages.length
                    ? <OnboardingProgress stages={stageBarsFor(assignment.stages)} />
                    : <p className="small text-body-secondary mb-0">No tasks have been materialised for this assignment.</p>}
                  <div className="d-flex gap-3 mt-2 align-items-center flex-wrap">
                    {canViewDetail(assignment) && assignment.stages.length > 0 && (
                      <button
                        className="btn btn-sm btn-link p-0"
                        aria-expanded={expandedId === assignment.id}
                        onClick={() => void toggleDetail(assignment)}
                      >
                        {expandedId === assignment.id ? "Hide details" : "View details"}
                      </button>
                    )}
                    {canManage && (
                      <>
                        {assignment.status !== "cancelled" && (
                          <button className="btn btn-sm btn-link text-danger p-0" disabled={busyId === assignment.id} onClick={() => void unassign(assignment)}>
                            Unassign
                          </button>
                        )}
                        <button className="btn btn-sm btn-link text-danger p-0" disabled={busyId === assignment.id} onClick={() => void remove(assignment)}>
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                  {expandedId === assignment.id && (
                    detailById[assignment.id] === "error"
                      ? <p className="small text-danger mt-2 mb-0">Could not load detailed progress.</p>
                      : detailById[assignment.id]
                        ? <AssignmentDetailView detail={detailById[assignment.id] as AssignmentDetail} onTaskTracked={(taskId, result) => patchTracked(assignment.id, taskId, result)} />
                        : <Skeleton label="Loading task detail…" variant="list" />
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function OnboardingPage({ teamId, userId, members, teams }: {
  teamId: string; userId: string; members: Member[]; teams: Array<{ id: string; name: string }>;
}) {
  const [summaries, setSummaries] = useState<AssignmentSummary[] | null>(null);
  const [details, setDetails] = useState<AssignmentDetail[]>([]);
  const [canAuthor, setCanAuthor] = useState(false);
  const [canAssign, setCanAssign] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<"mine" | "reviewing" | "progress" | "authoring">("mine");

  const load = useCallback(async () => {
    const [list, plans] = await Promise.all([
      api<{ assignments: AssignmentSummary[]; canAssign: boolean }>(`/api/onboarding/assignments?teamId=${encodeURIComponent(teamId)}`),
      api<{ canAuthor: boolean }>(`/api/onboarding/plans?teamId=${encodeURIComponent(teamId)}`),
    ]);
    setSummaries(list.assignments);
    setCanAuthor(plans.canAuthor);
    setCanAssign(list.canAssign);
    const full = await Promise.all(
      list.assignments.map((summary) => api<AssignmentDetail>(`/api/onboarding/assignments/${encodeURIComponent(summary.id)}`)),
    );
    setDetails(full);
  }, [teamId]);

  useEffect(() => {
    void load().catch((reason) => setError(reason instanceof Error ? reason.message : "Could not load onboarding."));
  }, [load]);

  const mine = useMemo(() => details.filter((detail) => detail.assignment.userId === userId), [details, userId]);
  const reviewing = useMemo(() => details.filter((detail) => detail.canReview && detail.assignment.userId !== userId), [details, userId]);
  const isMentor = useMemo(() => details.some((detail) => detail.assignment.mentorUserId === userId), [details, userId]);
  const canSeeProgress = canAssign || isMentor;

  if (error) return <ErrorBanner>{error}</ErrorBanner>;
  if (!summaries) return <Skeleton label="Loading onboarding…" variant="list" />;

  const tabs: Array<{ id: typeof tab; label: string; badge?: number }> = [{ id: "mine", label: "My onboarding" }];
  if (reviewing.length) tabs.push({ id: "reviewing", label: "Reviewing", badge: reviewing.reduce((count, detail) => count + detail.stages.flatMap((s) => s.tasks).filter((t) => t.state === "submitted").length + detail.quizzes.filter((q) => q.gradableAttemptId).length, 0) });
  if (canSeeProgress) tabs.push({ id: "progress", label: "Team progress" });
  if (canAuthor) tabs.push({ id: "authoring", label: "Authoring" });
  const activeTab = tabs.some((entry) => entry.id === tab) ? tab : "mine";

  if (!summaries.length && !canAuthor && !canSeeProgress) {
    return (
      <div className="text-center text-body-secondary py-5">
        <p className="mb-1">No onboarding plan is assigned to you on this team.</p>
        <p className="small mb-0">A team lead can assign one from the Authoring tab.</p>
      </div>
    );
  }

  return (
    <div>
      {tabs.length > 1 && (
        <ul className="nav nav-pills mb-3">
          {tabs.map((entry) => (
            <li className="nav-item" key={entry.id}>
              <button className={`nav-link ${activeTab === entry.id ? "active" : ""}`} onClick={() => setTab(entry.id)}>
                {entry.label}
                {entry.badge != null && <span className="badge text-bg-secondary ms-1">{entry.badge}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {activeTab === "mine" && (
        mine.length
          ? <div className="d-flex flex-column gap-4">{mine.map((detail) => <LearnerAssignment key={detail.assignment.id} detail={detail} onChanged={load} />)}</div>
          : <p className="text-body-secondary">You have no onboarding plan assigned.</p>
      )}
      {activeTab === "reviewing" && <ReviewQueue details={reviewing} onChanged={load} />}
      {activeTab === "progress" && <TeamProgress canManage={canAssign} teamId={teamId} userId={userId} />}
      {activeTab === "authoring" && <OnboardingAuthoring teamId={teamId} members={members} teams={teams} onAssigned={load} />}
    </div>
  );
}
